// ============================================================================
// warlords.js — the named officers. Six Magmar warlords with grimdark titles,
// the Magmar Dynast (victory target) in the citadel, and the allied Warmaster
// (defeat condition) in the war-camp. Each is a normal UNIT_WARLORD carried in
// the units pool + spatial hash, with a `warlord` descriptor for the HUD and
// unique reach / attack pattern. This module also tracks which warlord Benjen
// is currently engaging so the HUD can raise its health bar.
// ============================================================================

import { UNIT_WARLORD, TEAM_MAGMAR, TEAM_PLAYER } from '../core/constants.js';
import { CITADEL } from '../world/battlefield.js';
import { dist2 } from '../core/math.js';
import { EV } from '../core/events.js';

export const WARLORD_ROSTER = [
  { name: 'Ashlord Kaevun',   title: 'Breaker of the Ninth Host', hp: 1100, reach: 3.6, guardBreakPhase: 3, x: -96, z: -48, weapon: 'halberd' },
  { name: 'Vurmog',           title: 'the Cinder-Crowned',        hp: 1000, reach: 3.3, guardBreakPhase: 2, x:  96, z: -48, weapon: 'cleaver' },
  { name: 'Draxa Embermaw',   title: 'Warden of Slag',            hp: 1250, reach: 4.0, guardBreakPhase: 3, x: -96, z:  56, weapon: 'scythe' },
  { name: 'Tholvar',          title: 'the Molten Oath',           hp: 980,  reach: 3.2, guardBreakPhase: 1, x:  96, z:  56, weapon: 'twin' },
  { name: 'Skarn Duraghast',  title: 'Ninefold Ruin',             hp: 1400, reach: 4.2, guardBreakPhase: 3, x: -34, z: -104, weapon: 'maul' },
  { name: 'Ysra Coalheart',   title: 'the Widowing Flame',        hp: 1050, reach: 3.4, guardBreakPhase: 2, x:  34, z: -104, weapon: 'spear' },
];

function makeWarlord(world, team, x, z, name, title, hp, reach, gbPhase, weapon) {
  const u = world.spawnUnit(team, UNIT_WARLORD, x, z);
  if (!u) return null;
  u.hp = u.maxHp = hp;
  u.atkRange = reach;
  u.homeX = x; u.homeZ = z;
  u.warlord = { name, title, guardBreakPhase: gbPhase, reach, weapon: weapon || 'greatsword' };
  return u;
}

// Spawn one named Magmar warlord from the roster (optionally at a given spot).
// HP scales with the level's difficulty mods.
export function spawnWarlordByName(world, name, pos) {
  const w = WARLORD_ROSTER.find((r) => r.name === name);
  if (!w) return null;
  const x = pos ? pos.x : w.x, z = pos ? pos.z : w.z;
  const u = makeWarlord(world, TEAM_MAGMAR, x, z, w.name, w.title, Math.round(w.hp * world.mods.enemyHp), w.reach, w.guardBreakPhase, w.weapon);
  if (u) world.warlords.push(u);
  return u;
}

// The Magmar Dynast — the campaign's final target.
export function spawnDynast(world, pos) {
  const x = pos ? pos.x : CITADEL.x, z = pos ? pos.z : CITADEL.z - 6;
  const d = makeWarlord(world, TEAM_MAGMAR, x, z,
    'Ourang-Vael', 'the Undying Forge, Dynast of Magmar', Math.round(3200 * world.mods.enemyHp), 4.4, 3, 'dynast');
  if (d) { d.scale *= 1.25; world.dynast = d; }
  return d;
}

// track the warlord Benjen is fighting for the top-of-screen health bar
export function updateWarlordEngagement(world) {
  const p = world.player;
  let best = null, bestD = 26 * 26;
  for (let i = 0; i < world.warlords.length; i++) {
    const w = world.warlords[i];
    if (!w._active || w.state === 7 /* ST_DEAD */) continue;
    const d = dist2(p.x, p.z, w.x, w.z);
    if (d < bestD) { bestD = d; best = w; }
  }
  // also allow engaging the Dynast
  if (world.dynast && world.dynast._active && world.dynast.state !== 7) {
    const d = dist2(p.x, p.z, world.dynast.x, world.dynast.z);
    if (d < bestD) { bestD = d; best = world.dynast; }
  }

  if (best !== world.engagedWarlord) {
    world.engagedWarlord = best;
    if (best && !best.engaged) {
      best.engaged = true;
      world.events.emit(EV.WARLORD_ENGAGED, { warlord: best });
    }
  }
}
